package app.footyguide.web.components

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import app.footyguide.web.route.navigateTo

/**
 * Universal Unified Toolbar for all FootyGuide screens.
 * - Always 100% viewport width (full-bleed background)
 * - Fixed 56px height (never jumps or stretches)
 * - Inner content centered with max-width: 600px and 16px padding
 * - 100% tenant-aware: all accent colors, badges, and icons follow var(--accent-fill)
 */
@Composable
fun AppToolbar(
    onBack: (() -> Unit)? = null,
    isBrand: Boolean = false,
    icon: (@Composable () -> Unit)? = null,
    title: String? = null,
    subtitle: String? = null,
    actions: (@Composable () -> Unit)? = null
) {
    Header({
        style {
            position(Position.Sticky)
            top(0.px)
            property("z-index", "100")
            width(100.percent)
            height(56.px)
            minHeight(56.px)
            maxHeight(56.px)
            backgroundColor(Color("var(--surface, #FFFFFF)"))
            property("border-bottom", "1px solid var(--border, #E7E7E1)")
            property("box-shadow", "0 1px 2px rgba(22, 24, 29, 0.03)")
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(0.px)
            boxSizing("border-box")
        }
    }) {
        Div({
            style {
                width(100.percent)
                maxWidth(600.px)
                height(100.percent)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                padding(0.px, 16.px)
                boxSizing("border-box")
            }
        }) {
            // LEFT / TITLE BLOCK
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(10.px)
                    height(100.percent)
                }
            }) {
                // Optional Back button
                if (onBack != null) {
                    Button({
                        id("btn-toolbar-back")
                        onClick { onBack() }
                        style {
                            background("transparent")
                            border(0.px)
                            padding(6.px)
                            marginLeft((-6).px)
                            cursor("pointer")
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            borderRadius(8.px)
                        }
                    }) {
                        LucideIcon(Icons.ARROW_LEFT, size = 20, color = "#16181D")
                    }
                }

                if (isBrand) {
                    // Official Brand Logo (Dynamic Club/Default Colored Symbol + Bold Solid Footy Guide)
                    A(href = app.footyguide.web.route.WebRoutes.HOME, {
                        id("brand-logo-link")
                        onClick { event ->
                            event.preventDefault()
                            navigateTo(app.footyguide.web.route.WebRoutes.HOME)
                        }
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(8.px)
                            height(100.percent)
                            property("text-decoration", "none")
                            cursor("pointer")
                        }
                    }) {
                        Span({
                            id("brand-symbol-icon")
                            style {
                                display(DisplayStyle.InlineBlock)
                                width(26.px)
                                height(26.px)
                                minWidth(26.px)
                                backgroundColor(Color("var(--accent-fill, #00E676)"))
                                property("mask-image", "url('/assets/branding/app-symbol-pure.png?v=12')")
                                property("-webkit-mask-image", "url('/assets/branding/app-symbol-pure.png?v=12')")
                                property("mask-size", "contain")
                                property("-webkit-mask-size", "contain")
                                property("mask-repeat", "no-repeat")
                                property("-webkit-mask-repeat", "no-repeat")
                                property("mask-position", "center")
                                property("-webkit-mask-position", "center")
                                property("transition", "background-color 0.3s ease")
                            }
                        })
                        Img(
                            src = "/assets/branding/logo-text-solid.png?v=12",
                            alt = "FootyGuide"
                        ) {
                            style {
                                height(20.px)
                                property("object-fit", "contain")
                                display(DisplayStyle.Block)
                            }
                        }
                    }
                } else {
                    icon?.invoke()

                    if (title != null) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                            }
                        }) {
                            Span({
                                style {
                                    fontFamily("Sora, sans-serif")
                                    fontSize(16.px)
                                    fontWeight(800)
                                    color(Color("#16181D"))
                                    letterSpacing((-0.4).px)
                                }
                            }) {
                                Text(title)
                            }
                            if (subtitle != null) {
                                Span({
                                    style {
                                        fontSize(11.px)
                                        fontFamily("Inter, sans-serif")
                                        color(Color("#71717A"))
                                        fontWeight(500)
                                    }
                                }) {
                                    Text(subtitle)
                                }
                            }
                        }
                    }
                }
            }

            // RIGHT / ACTIONS BLOCK
            if (actions != null) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(12.px)
                    }
                }) {
                    actions()
                }
            }
        }
    }
}

/**
 * Backward compatibility alias for HomePage toolbar.
 */
@Composable
fun Toolbar(
    onSearchClick: () -> Unit = {},
    dateLabel: String? = null,
    onDateLabelClick: () -> Unit = {},
    teamBadge: (@Composable () -> Unit)? = null
) {
    AppToolbar(
        isBrand = true,
        actions = {
            if (dateLabel != null) {
                Button({
                    onClick { onDateLabelClick() }
                    style {
                        fontSize(11.px)
                        fontWeight(800)
                        fontFamily("Sora, 'Plus Jakarta Sans', sans-serif")
                        color(Color("var(--accent-fill, #0B6E4F)"))
                        letterSpacing(0.5.px)
                        property("text-transform", "uppercase")
                        padding(6.px, 12.px)
                        borderRadius(999.px)
                        backgroundColor(Color("rgba(var(--accent-fill-rgb, 11, 110, 79), 0.08)"))
                        border(1.px, LineStyle.Solid, Color("rgba(var(--accent-fill-rgb, 11, 110, 79), 0.25)"))
                        property("appearance", "none")
                        property("-webkit-appearance", "none")
                        cursor("pointer")
                        property("transition", "all 0.2s ease")
                    }
                }) {
                    Text(dateLabel)
                }
            }
            teamBadge?.invoke()
        }
    )
}
